'use client'

import { useMemo } from 'react'

import type { AllListingsFilterElement } from '@b-stock/search-api-client'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import toDisplayNames from '@/components/listings/search/SearchFilters/toDisplayNames'

import AppliedFilterSection from '../../shared/AppliedFilterSection'
import type { AppliedProps } from '../../types'

const label = 'Filters.storefront'

// Stable reference so `storefrontFacet` below doesn't get a fresh `[]` every
// render when the facet hasn't loaded yet — a new array reference each time
// would defeat the useMemo below (it'd never see this dependency as unchanged).
const EMPTY_STOREFRONT_FACET: AllListingsFilterElement[] = []

/**
 * `storeFrontId` (bare storefront ids, populated only via deep-link — e.g.
 * the Auction Schedule click-through) has no sidebar picker of its own. It
 * shares the "Seller" chip section/label with the existing `storefront`
 * filter for visual consistency, but resolves its display name by matching
 * the bare id against the storefront facet's composite `"<id>-<name>"`
 * values, falling back to the raw id if no match is found (e.g. facet not
 * yet loaded, or the seller has no other matching listings right now).
 */
const StoreFrontIdFilterApplied = ({
  filtersState,
  updateFilters,
  noClose,
}: AppliedProps) => {
  const { data } = useListingSearch()
  const storefrontFacet = data?.allFilters.storefront ?? EMPTY_STOREFRONT_FACET

  const values = useMemo(
    () =>
      filtersState.storeFrontId.map((id) => {
        // Assumes storefront ids never contain an internal dash (they're
        // Mongo ObjectIds or short integ codes, per toDisplayNames.storefront's
        // own parsing comment) — under that invariant `startsWith` can't
        // false-match a different id's entry: two dash-free ids can only
        // collide this way if they're identical. If that ever stops holding
        // (a hyphenated id), a robust fix needs the API to expose id and
        // name as separate fields — `AllListingsFilterElement` only has
        // `value`/`count`, so no client-side string heuristic can fully
        // disambiguate "id-with-a-dash-Name" from "id-Name-that-has-a-dash".
        const facetEntry = storefrontFacet.find((entry) =>
          entry.value.startsWith(`${id}-`)
        )
        return {
          key: id,
          name: facetEntry ? toDisplayNames.storefront(facetEntry.value) : id,
        }
      }),
    [filtersState.storeFrontId, storefrontFacet]
  )

  const onRemove = (idToRemove: string) => {
    updateFilters({
      storeFrontId: filtersState.storeFrontId.filter((id) => id !== idToRemove),
    })
  }

  return (
    <AppliedFilterSection
      label={label}
      values={values}
      onRemove={onRemove}
      noClose={noClose}
    />
  )
}

const StoreFrontIdFilter = {
  Applied: StoreFrontIdFilterApplied,
}

export default StoreFrontIdFilter
